import { useCallback } from 'react'
import { useUserStore } from '@/entities/user'
import { ApiError, queryClient } from '@/shared/api'
import { broadcastAuthEvent } from '@/shared/lib/auth-sync'
import { authApi } from '../api'

export type AuthCredentials = {
  email: string
  password: string
  acceptedUserAgreement?: boolean
}

export type AuthProfileInput = {
  name?: string
  telegram?: string
  company?: string
  position?: string
  acceptedUserAgreement?: boolean
}

const isUnauthorizedAuthError = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.status === 403)

const clearAuthScopedQueryState = () => {
  queryClient.clear()
}

export function useAuth() {
  const { user, isAuthenticated, isLoading, setUser, setLoading, clearUser } = useUserStore()

  const login = useCallback(
    async (credentials: AuthCredentials) => {
      const response = await authApi.login(credentials)
      setUser(response.user)
      return response
    },
    [setUser]
  )

  const register = useCallback(
    async (credentials: AuthCredentials) => {
      const response = await authApi.register(credentials)
      setUser(response.user)
      return response
    },
    [setUser]
  )

  const getConsentState = useCallback(async (email: string) => {
    return await authApi.getConsentState(email)
  }, [])

  const updateProfile = useCallback(
    async (input: AuthProfileInput) => {
      const response = await authApi.updateProfile(input)
      setUser(response.user)
      return response
    },
    [setUser]
  )

  const requestPasswordReset = useCallback(async (email: string) => {
    return await authApi.requestPasswordReset({ email })
  }, [])

  const confirmPasswordReset = useCallback(
    async (input: { token: string; password: string }) => {
      const response = await authApi.confirmPasswordReset(input)
      setUser(response.user)
      return response
    },
    [setUser]
  )

  const requestMagicLink = useCallback(async (email: string) => {
    return await authApi.requestMagicLink({ email })
  }, [])

  const consumeMagicLink = useCallback(
    async (token: string) => {
      const response = await authApi.consumeMagicLink(token)
      setUser(response.user)
      return response
    },
    [setUser]
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    clearAuthScopedQueryState()
    clearUser()
    broadcastAuthEvent('logout')
  }, [clearUser])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const response = await authApi.me()
      setUser(response.user)
      return response
    } catch (error) {
      if (isUnauthorizedAuthError(error)) {
        clearAuthScopedQueryState()
        clearUser()
        broadcastAuthEvent('logout')
      }
      return null
    } finally {
      setLoading(false)
    }
  }, [clearUser, setLoading, setUser])

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    getConsentState,
    updateProfile,
    requestPasswordReset,
    confirmPasswordReset,
    requestMagicLink,
    consumeMagicLink,
    logout,
    refresh,
  }
}
